import type { GridColumnResizeParams, GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import { useCallback, useState } from 'react';
import { z } from 'zod';

const DataSourceGridStateSchema = z.object({
  columnWidths: z.record(z.number()).optional(),
  columnVisibility: z.record(z.boolean()).optional(),
  columnOrder: z.array(z.string()).optional(),
});

type DataSourceGridState = z.infer<typeof DataSourceGridStateSchema>;

const getStorageKey = (dataSourceId: number) => `dataSources:${dataSourceId}`;

const getStoredState = (dataSourceId: number): DataSourceGridState | null => {
  try {
    const stored = localStorage.getItem(getStorageKey(dataSourceId));
    if (!stored) return null;
    const result = DataSourceGridStateSchema.safeParse(JSON.parse(stored));
    if (!result.success) {
      console.error('Invalid stored grid state:', result.error);
      return null;
    }
    return result.data;
  } catch (error) {
    console.error('Failed to get stored grid state:', error);
    return null;
  }
};

const setStoredState = (dataSourceId: number, state: Partial<DataSourceGridState>) => {
  try {
    const existing = getStoredState(dataSourceId) ?? {};
    localStorage.setItem(getStorageKey(dataSourceId), JSON.stringify({ ...existing, ...state }));
  } catch (error) {
    console.error('Failed to store grid state:', error);
  }
};

export const useDataSourceGridState = (dataSourceId: number) => {
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    () => getStoredState(dataSourceId)?.columnWidths ?? {},
  );

  const [columnVisibility, setColumnVisibility] = useState<GridColumnVisibilityModel>(
    () => getStoredState(dataSourceId)?.columnVisibility ?? {},
  );

  const [columnOrder, setColumnOrder] = useState<string[] | undefined>(
    () => getStoredState(dataSourceId)?.columnOrder,
  );

  const onColumnWidthChange = useCallback(
    (params: GridColumnResizeParams) => {
      setColumnWidths((prev) => {
        const newWidths = {
          ...prev,
          [params.colDef.field]: params.width,
        };
        setStoredState(dataSourceId, { columnWidths: newWidths });
        return newWidths;
      });
    },
    [dataSourceId],
  );

  const onColumnVisibilityChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      setColumnVisibility(model);
      setStoredState(dataSourceId, { columnVisibility: model });
    },
    [dataSourceId],
  );

  const onColumnOrderChange = useCallback(
    (newOrder: string[]) => {
      setColumnOrder(newOrder);
      setStoredState(dataSourceId, { columnOrder: newOrder });
    },
    [dataSourceId],
  );

  return {
    columnOrder,
    columnVisibility,
    columnWidths,
    onColumnOrderChange,
    onColumnVisibilityChange,
    onColumnWidthChange,
  };
};
